/**
 * Recent File Handle Store
 *
 * FileSystemFileHandle objects are structured-cloneable but not
 * JSON-serialisable, so the document store's `recents` slice (persisted via
 * localStorage) cannot hold them directly. This small IndexedDB side-store
 * keeps the native handle for every recent entry that lives on the real
 * file system; the recents list stores the entry id. On open,
 * `queryPermission` asks the browser to re-grant read access (Chromium may
 * require a fresh user gesture / prompt for cross-session handles — the
 * caller surfaces a re-pick fallback when permission is denied).
 */

const DB_NAME = 'yap-recent-handles';
const DB_VERSION = 1;
const STORE = 'handles';

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** Stores (or replaces) the native handle for a recent entry id. */
export async function saveRecentHandle(id: string, handle: FileSystemFileHandle): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put({ id, handle });
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    });
}

/** Reads the stored handle, or null. */
export async function getRecentHandle(id: string): Promise<FileSystemFileHandle | null> {
    const db = await openDatabase();
    return new Promise<FileSystemFileHandle | null>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const request = tx.objectStore(STORE).get(id) as IDBRequest<{ handle?: FileSystemFileHandle } | undefined>;
        request.onsuccess = () => { db.close(); resolve(request.result?.handle ?? null); };
        request.onerror = () => { db.close(); reject(request.error); };
    });
}

/**
 * Drops every parked handle. Used when the recents list is emptied;
 * individual removals are unnecessary because re-pushing a recent reuses
 * its stable id (upsert) and stale ids are simply never looked up again.
 */
export async function clearRecentHandles(): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).clear();
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    });
}

export async function deleteRecentHandle(id: string): Promise<void> {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(id);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    });
}

/**
 * Ensures the handle still grants read access after a fresh page load.
 * Returns false when the browser denies/asks and the user does not grant —
 * callers must then fall back to the Open dialog.
 */
export async function verifyReadPermission(handle: FileSystemFileHandle): Promise<boolean> {
    try {
        const permHandle = handle as FileSystemHandle & {
            queryPermission?: (d: { mode: string }) => Promise<PermissionState>;
            requestPermission?: (d: { mode: string }) => Promise<PermissionState>;
        };
        if (await permHandle.queryPermission?.({ mode: 'read' }) === 'granted') return true;
        return (await permHandle.requestPermission?.({ mode: 'read' })) === 'granted';
    } catch {
        return false;
    }
}
